import React from 'react';
import { Redirect } from 'react-router';
import { Route, Switch } from 'react-router-dom';

import { RouteConfig } from 'routes/route-config';

// general.
import { Dashboard } from 'pages/dashboard';
import { Error404Page } from 'pages/error/404';

// projects.
import { ProjectsPage } from './components/projects';

// analytics.
import { AnalyticsSessions } from './components/analytics/analytics-sessions';
import { AnalyticsStops } from './components/analytics/analytics-stops';
import { AnalyticsTours } from './components/analytics/analytics-tours';
import { AnalyticsVisitorTraffic } from './components/analytics/analytics-visitor-traffic';

// stops.
import { StopsPage } from './components/stops';
import { StopUpdate } from './components/stops/stop-update';
import { MediaCarousel } from './components/stops/stop-media-carousel';
import { CarouselItemUpdate } from './components/stops/stop-media-carousel/media-carousel-item-update';
import { StopTranslations } from './components/stops/stop-translations';
import { RelatedStops } from './components/stops/stop-related-stops';

// tours.
import { ToursPage } from './components/tours';
import { TourUpdate } from './components/tours/tour-update';
import { TourUpdateDetails } from './components/tours/tour-update-details';
import { TourTranslations } from './components/tours/tour-translations';

// moods.
import { MoodsPage } from './components/moods';
import { MoodUpdate } from './components/moods/mood-update';
import { MoodUpdateDetails } from './components/moods/mood-update-details';
import { MoodTranslations } from './components/moods/mood-translations';

// points of interest
import { PointOfInterestUpdate } from './components/points-of-interest/point-of-interest-update';
import { PointOfInterestTranslations } from './components/points-of-interest/point-of-interest-translations';

// generic points of interest
import { GenericPointOfInterestUpdate } from './components/generic-points-of-interest/generic-point-of-interest-update';
import { GenericPointOfInterestTranslations } from './components/generic-points-of-interest/generic-point-of-interest-translations';

// event points of interest
import { EventPointOfInterestUpdate } from './components/event-points-of-interest/event-point-of-interest-update';
import { EventPointOfInterestTranslations } from './components/event-points-of-interest/event-point-of-interest-translations';

// visitor types
import { VisitorTypes } from './components/visitor-types';
import { VisitorTypeUpdate } from './components/visitor-types/visitor-type-update';
import { VisitorTypeTranslations } from './components/visitor-types/visitor-type-translations';

// tour categories.
import { CategoriesPage } from './components/categories';
import { CategoryTranslations } from './components/categories/category-translations';

// visitor info
import { VisitorInfo } from './components/visitor-info';
import { AmenityUpdate } from './components/visitor-info/amenity-update';
import { VisitorInfoUpdate } from './components/visitor-info/visitor-info-update';
import { VisitorInfoTranslations } from './components/visitor-info/visitor-info-translations';
import { AmenityTranslations } from './components/visitor-info/amenity-translations';

// publishing
import { PublishingPage } from './components/publishing';
import { ReleasesPage } from './components/publishing/releases';

const GettyRoutes = () => (
  <Switch>
    {/* stops. */}
    <Route
      exact
      path="/project/:projectId"
      render={({ match }) => (
        <Redirect to={{ pathname: `/project/${match.params.projectId}/stops` }} />
      )}
    />
    <Route
      exact
      path="/project/:projectId/stops"
      render={({ match, history }) => (
        <StopsPage projectId={match.params.projectId} navBackRoute="/" historyPush={history.push} />
      )}
    />
    <Route
      exact
      path="/project/:projectId/stop/:stopId"
      render={({ history, match }) => (
        <StopUpdate
          historyPush={history.push}
          projectId={match.params.projectId}
          stopId={match.params.stopId}
        />
      )}
    />
    <Route
      exact
      path="/project/:projectId/stop/:stopId/media-carousel"
      render={({ history, match }) => (
        <MediaCarousel
          historyPush={history.push}
          projectId={match.params.projectId}
          stopId={match.params.stopId}
          tourId={match.params.tourId}
        />
      )}
    />
    <Route
      exact
      path="/project/:projectId/stop/:stopId/media-carousel/:carouselItemId"
      render={({ history, match }) => (
        <CarouselItemUpdate
          historyPush={history.push}
          projectId={match.params.projectId}
          stopId={match.params.stopId}
          tourId={match.params.tourId}
          carouselItemId={match.params.carouselItemId}
        />
      )}
    />
    <Route
      exact
      path="/project/:projectId/stop/:stopId/translations"
      render={({ history, match }) => (
        <StopTranslations
          historyPush={history.push}
          projectId={match.params.projectId}
          stopId={match.params.stopId}
        />
      )}
    />
    <Route
      exact
      path="/project/:projectId/stop/:stopId/related-stops"
      render={({ history, match }) => (
        <RelatedStops
          historyPush={history.push}
          projectId={match.params.projectId}
          stopId={match.params.stopId}
          tourId={match.params.tourId}
        />
      )}
    />

    {/* tours. */}
    <Route
      exact
      path="/project/:projectId/tours"
      render={({ match, history }) => (
        <ToursPage projectId={match.params.projectId} navBackRoute="/" historyPush={history.push} />
      )}
    />
    <Route
      exact
      path="/project/:projectId/tour/:tourId"
      render={({ match, history }) => (
        <TourUpdate
          projectId={match.params.projectId}
          tourId={match.params.tourId}
          historyPush={history.push}
        />
      )}
    />
    <Route
      exact
      path="/project/:projectId/tour/:tourId/tour-details"
      render={({ match, history }) => (
        <TourUpdateDetails
          projectId={match.params.projectId}
          tourId={match.params.tourId}
          historyPush={history.push}
        />
      )}
    />
    <Route
      exact
      path="/project/:projectId/tour/:tourId/translations"
      render={({ history, match }) => (
        <TourTranslations
          historyPush={history.push}
          projectId={match.params.projectId}
          tourId={match.params.tourId}
        />
      )}
    />

    {/* stops within tours. */}
    <Route
      exact
      path="/project/:projectId/tour/:tourId/stop/:stopId"
      render={({ history, match }) => (
        <StopUpdate
          historyPush={history.push}
          projectId={match.params.projectId}
          stopId={match.params.stopId}
          tourId={match.params.tourId}
        />
      )}
    />
    <Route
      exact
      path="/project/:projectId/tour/:tourId/stop/:stopId/media-carousel"
      render={({ history, match }) => (
        <MediaCarousel
          historyPush={history.push}
          projectId={match.params.projectId}
          stopId={match.params.stopId}
          tourId={match.params.tourId}
        />
      )}
    />
    <Route
      exact
      path="/project/:projectId/tour/:tourId/stop/:stopId/media-carousel/:carouselItemId"
      render={({ history, match }) => (
        <CarouselItemUpdate
          historyPush={history.push}
          projectId={match.params.projectId}
          stopId={match.params.stopId}
          tourId={match.params.tourId}
          carouselItemId={match.params.carouselItemId}
        />
      )}
    />
    <Route
      exact
      path="/project/:projectId/tour/:tourId/stop/:stopId/translations"
      render={({ history, match }) => (
        <StopTranslations
          historyPush={history.push}
          projectId={match.params.projectId}
          stopId={match.params.stopId}
          tourId={match.params.tourId}
        />
      )}
    />
    <Route
      exact
      path="/project/:projectId/tour/:tourId/stop/:stopId/related-stops"
      render={({ history, match }) => (
        <RelatedStops
          historyPush={history.push}
          projectId={match.params.projectId}
          stopId={match.params.stopId}
        />
      )}
    />

    <Route
      exact
      path="/project/:projectId/moods"
      render={({ match, history }) => (
        <MoodsPage projectId={match.params.projectId} navBackRoute="/" historyPush={history.push} />
      )}
    />
    <Route
      exact
      path="/project/:projectId/mood/:moodId"
      render={({ match, history }) => (
        <MoodUpdate
          projectId={match.params.projectId}
          moodId={match.params.moodId}
          historyPush={history.push}
        />
      )}
    />

    <Route
      exact
      path="/project/:projectId/visitor-types"
      render={({ match, history }) => (
        <VisitorTypes projectId={match.params.projectId} navBackRoute="/" historyPush={history.push} />
      )}
    />

    <Route
      exact
      path="/project/:projectId/visitor-type/:visitorTypeId/translations"
      render={({ history, match }) => (
        <VisitorTypeTranslations
          historyPush={history.push}
          projectId={match.params.projectId}
          visitorTypeId={match.params.visitorTypeId}
        />
      )}
    />

    <Route
      exact
      path="/project/:projectId/visitor-type/:visitorTypeId"
      render={({ history, match }) => (
        <VisitorTypeUpdate
          historyPush={history.push}
          projectId={match.params.projectId}
          visitorTypeId={match.params.visitorTypeId}
        />
      )}
    />


    <Route
      exact
      path="/project/:projectId/mood/:moodId/generic-point-of-interest/:pointOfInterestId/translations"
      render={({ history, match }) => (
        <GenericPointOfInterestTranslations
          historyPush={history.push}
          projectId={match.params.projectId}
          pointOfInterestId={match.params.pointOfInterestId}
          moodId={match.params.moodId}
        />
      )}
    />

    <Route
      exact
      path="/project/:projectId/mood/:moodId/generic-point-of-interest/:pointOfInterestId"
      render={({ history, match }) => (
        <GenericPointOfInterestUpdate
          historyPush={history.push}
          projectId={match.params.projectId}
          pointOfInterestId={match.params.pointOfInterestId}
          moodId={match.params.moodId}
        />
      )}
    />

    <Route
      exact
      path="/project/:projectId/mood/:moodId/event-point-of-interest/:pointOfInterestId/translations"
      render={({ history, match }) => (
        <EventPointOfInterestTranslations
          historyPush={history.push}
          projectId={match.params.projectId}
          pointOfInterestId={match.params.pointOfInterestId}
          moodId={match.params.moodId}
        />
      )}
    />

    <Route
      exact
      path="/project/:projectId/mood/:moodId/event-point-of-interest/:pointOfInterestId"
      render={({ history, match }) => (
        <EventPointOfInterestUpdate
          historyPush={history.push}
          projectId={match.params.projectId}
          pointOfInterestId={match.params.pointOfInterestId}
          moodId={match.params.moodId}
        />
      )}
    />

    <Route
      exact
      path="/project/:projectId/mood/:moodId/point-of-interest/:pointOfInterestId/translations"
      render={({ history, match }) => (
        <PointOfInterestTranslations
          historyPush={history.push}
          projectId={match.params.projectId}
          pointOfInterestId={match.params.pointOfInterestId}
          moodId={match.params.moodId}
        />
      )}
    />

    <Route
      exact
      path="/project/:projectId/mood/:moodId/point-of-interest/:pointOfInterestId"
      render={({ history, match }) => (
        <PointOfInterestUpdate
          historyPush={history.push}
          projectId={match.params.projectId}
          pointOfInterestId={match.params.pointOfInterestId}
          moodId={match.params.moodId}
        />
      )}
    />

    <Route
      exact
      path="/project/:projectId/mood/:moodId/mood-details"
      render={({ match, history }) => (
        <MoodUpdateDetails
          projectId={match.params.projectId}
          moodId={match.params.moodId}
          historyPush={history.push}
        />
      )}
    />
    <Route
      exact
      path="/project/:projectId/mood/:moodId/translations"
      render={({ history, match }) => (
        <MoodTranslations
          historyPush={history.push}
          projectId={match.params.projectId}
          moodId={match.params.moodId}
        />
      )}
    />

    {/* tour categories. */}
    <Route
      exact
      path="/project/:projectId/categories"
      render={({ history, match }) => (
        <CategoriesPage historyPush={history.push} projectId={match.params.projectId} />
      )}
    />
    <Route
      exact
      path="/project/:projectId/categories/translations"
      render={({ history, match }) => (
        <CategoryTranslations historyPush={history.push} projectId={match.params.projectId} />
      )}
    />
    {/* visitor info. */}
    <Route
      exact
      path="/project/:projectId/visitor-info"
      render={({ history, match }) => (
        <VisitorInfo historyPush={history.push} projectId={match.params.projectId} />
      )}
    />
    <Route
      exact
      path="/project/:projectId/visitor-info/amenity/:refId"
      render={({ history, match }) => (
        <AmenityUpdate
          historyPush={history.push}
          projectId={match.params.projectId}
          refId={match.params.refId}
        />
      )}
    />
    <Route
      exact
      path="/project/:projectId/visitor-info/:refId"
      render={({ history, match }) => (
        <VisitorInfoUpdate
          historyPush={history.push}
          projectId={match.params.projectId}
          refId={match.params.refId}
        />
      )}
    />
    <Route
      exact
      path="/project/:projectId/visitor-info/:refId/translations"
      render={({ history, match }) => (
        <VisitorInfoTranslations
          historyPush={history.push}
          projectId={match.params.projectId}
          refId={match.params.refId}
        />
      )}
    />
    <Route
      exact
      path="/project/:projectId/visitor-info/amenity/:refId/translations"
      render={({ history, match }) => (
        <AmenityTranslations
          historyPush={history.push}
          projectId={match.params.projectId}
          refId={match.params.refId}
        />
      )}
    />
    {/* publishing. */}
    <Route
      exact
      path="/project/:projectId/publishing"
      render={({ history, match }) => (
        <PublishingPage historyPush={history.push} projectId={match.params.projectId} />
      )}
    />
    <Route
      exact
      path="/project/:projectId/releases"
      render={({ history, match }) => (
        <ReleasesPage historyPush={history.push} projectId={match.params.projectId} />
      )}
    />
    <Route render={() => <Error404Page />} />
  </Switch>
);

const AnalyticsRoutes = () => (
  <Switch>
    <Route
      exact
      path="/analytics"
      render={({ match }) => <Redirect to={{ pathname: '/analytics/sessions' }} />}
    />
    <Route exact path="/analytics/sessions" render={() => <AnalyticsSessions />} />
    <Route exact path="/analytics/stops" render={() => <AnalyticsStops />} />
    <Route exact path="/analytics/tours" render={() => <AnalyticsTours />} />
    <Route exact path="/analytics/visitor-traffic" render={() => <AnalyticsVisitorTraffic />} />
    <Route render={() => <Error404Page />} />
  </Switch>
);

/* eslint-disable react/display-name */
export const gettyRoutes: ReadonlyArray<RouteConfig> = [
  {
    path: '/',
    exact: true,
    render: () => <ProjectsPage />,
  },
  {
    path: '/project',
    render: () => <GettyRoutes />,
  },
  {
    path: '/analytics',
    render: () => <AnalyticsRoutes />,
  },
  {
    path: '/sitemanagement',
    exact: true,
    render: () => <Dashboard />,
  },
];
/* eslint-enable react/display-name */
